import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { FilePreviews, FilePreview } from "../types/messages.types";
import { validateFile, getFileTypeCategory } from "../utils/messageUtils";
import { FILE_UPLOAD } from "../constants/messages.constants";

interface UseFileUploadReturn {
  attachments: (string | File)[];
  filePreviews: FilePreviews;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemovePreview: (chatId: string, index: number) => void;
  clearAttachments: () => void;
  setAttachments: React.Dispatch<React.SetStateAction<(string | File)[]>>;
  setFilePreviews: React.Dispatch<React.SetStateAction<FilePreviews>>;
}

/**
 * Custom hook for file upload handling
 * Manages file selection, validation, preview generation, and removal
 */
export const useFileUpload = (
  selectedChatId: string | null
): UseFileUploadReturn => {
  const [attachments, setAttachments] = useState<(string | File)[]>([]);
  const [filePreviews, setFilePreviews] = useState<FilePreviews>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Clear attachments when chat changes
   */
  useEffect(() => {
    if (selectedChatId) {
      setFilePreviews((prev) => ({
        ...prev,
        [selectedChatId]: [],
      }));
      setAttachments([]);
    }
  }, [selectedChatId]);

  /**
   * Handle file selection and preview generation
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedChatId) return;

    const chatId = selectedChatId;
    const validFiles: File[] = [];
    const newPreviews: FilePreview[] = [];

    files.forEach((file) => {
      // Validate file
      const validation = validateFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }

      const type = file.type;
      const category = getFileTypeCategory(type);

      // Process different file types
      if (category === "image") {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviews((prev) => ({
            ...prev,
            [chatId]: [
              ...(Array.isArray(prev[chatId]) ? prev[chatId] : []),
              { type: "image", content: reader.result as string, file },
            ],
          }));
        };
        reader.readAsDataURL(file);
        validFiles.push(file);
      } else if (category === "video") {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviews((prev) => ({
            ...prev,
            [chatId]: [
              ...(Array.isArray(prev[chatId]) ? prev[chatId] : []),
              { type: "video", content: reader.result as string, file },
            ],
          }));
        };
        reader.readAsDataURL(file);
        validFiles.push(file);
      } else if (category === "pdf") {
        newPreviews.push({ type: "pdf", file });
        validFiles.push(file);
      } else if (category === "doc") {
        newPreviews.push({ type: "doc", file });
        validFiles.push(file);
      } else {
        newPreviews.push({ type: "other", file });
        validFiles.push(file);
      }
    });

    // Add non-image/video previews
    if (newPreviews.length > 0) {
      setFilePreviews((prev) => ({
        ...prev,
        [chatId]: [
          ...(Array.isArray(prev[chatId]) ? prev[chatId] : []),
          ...newPreviews,
        ],
      }));
    }

    setAttachments((prev) => [...prev, ...validFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Remove a file preview and attachment
   */
  const handleRemovePreview = (chatId: string, indexToRemove: number) => {
    setFilePreviews((prev) => {
      const currentPreviews = prev[chatId] || [];
      const updatedPreviews = currentPreviews.filter(
        (_, i) => i !== indexToRemove
      );
      return {
        ...prev,
        [chatId]: updatedPreviews,
      };
    });

    setAttachments((prev) => {
      return prev.filter((_, i) => i !== indexToRemove);
    });
  };

  /**
   * Clear all attachments
   */
  const clearAttachments = () => {
    setAttachments([]);
    if (selectedChatId) {
      setFilePreviews((prev) => ({
        ...prev,
        [selectedChatId]: [],
      }));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    attachments,
    filePreviews,
    fileInputRef,
    handleFileChange,
    handleRemovePreview,
    clearAttachments,
    setAttachments,
    setFilePreviews,
  };
};
